'use client'; // Error components must be Client Components

import { useEffect } from 'react';
import { Button } from '@/components/ui/button';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error(error);

    // During rolling deploys, users can briefly receive stale HTML that points
    // to a chunk hash that no longer exists. Force a one-time hard reload to
    // fetch the latest HTML/chunk manifest and self-heal.
    const isChunkLoadError = /ChunkLoadError|Loading chunk\s+\d+\s+failed/i.test(error.message || "");
    if (!isChunkLoadError) {
      return;
    }

    const retryKey = "zamzi:chunk-reload-attempted";
    const alreadyRetried = typeof window !== "undefined" && window.sessionStorage.getItem(retryKey) === "1";
    if (alreadyRetried) {
      return;
    }

    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(retryKey, "1");
      window.location.reload();
    }
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground p-4">
      <div className="max-w-md text-center">
        <h1 className="text-4xl font-bold text-destructive mb-4">Application Error</h1>
        <p className="text-lg mb-2">Something went wrong.</p>
        <p className="text-sm text-muted-foreground mb-6">
          We are sorry for the inconvenience. The development team has been notified.
        </p>
        
        <div className="bg-muted p-4 rounded-md text-left mb-6">
          <p className="font-semibold">Error Details:</p>
          <pre className="text-xs whitespace-pre-wrap wrap-break-word mt-2 font-mono">
            {error.message}
          </pre>
          {error.digest && (
            <pre className="text-xs whitespace-pre-wrap wrap-break-word mt-2 font-mono">
              Digest: {error.digest}
            </pre>
          )}
        </div>

        <Button
          onClick={
            // Attempt to recover by trying to re-render the segment
            () => reset()
          }
        >
          Try again
        </Button>
      </div>
    </div>
  );
}
